import { clamp, cloneDeep, pullAllBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Message, Modal, Tooltip } from '@meitu/saas-ui';
import { BackTitle } from '@meitu/saas-ui/es/title';
import {
    cmToPx,
    getDPI,
    parseToPixels,
    parseToUnit,
    pxToCM,
    SizeUnitType,
} from '@meitu/saas-editor-core/es/utils/unit-kit';
import { ILayerProps, ISize } from '@meitu/saas-editor-core/es/version-types';
import {
    CHANGE_DPI_HEIGHT_EDGE,
    CHANGE_DPI_WIDTH_EDGE,
    MAX_STAGE_HEIGHT,
    MAX_STAGE_HEIGHT_CM,
    MAX_STAGE_WIDTH,
    MAX_STAGE_WIDTH_CM,
    MIN_STAGE_HEIGHT,
    MIN_STAGE_WIDTH,
} from '@meitu/saas-editor-core/es/version-types/constants';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { fetchSizeList } from '@/services/size-list';
import { ListType } from '@/services/size-list/index.d';
import { useTemplateModel } from '@/store/models/template-model';
import List from './components/list';
import SelectOpt from './components/select-opt';
import SizeInfo from './components/size-info';
import { CUSTOM_SIZE_ID, getCustomUnit, handleSizeListStructure, ORIGIN_SIZE_ID } from './utils';
import './index.less';
import { QuickAdjustOperate } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/stage-operate';
import IconFont from '@meitu/xdesign-icon-font';
import { events, REPLACE_TEMPLATE } from '@/events-bus';
import { isIntl } from '@/react-process-env';

const AdjustSizePanel: React.FC<{
    onClose?: Function;
    currentWidth: number;
    currentHeight: number;
    activeStageIndex: number;
}> = ({ onClose, currentWidth, currentHeight, activeStageIndex }) => {
    const { $t } = useLocaleStore();
    const [unit, setUnit] = useState(editor.services.editor.getUnit());
    const [sizeList, setSizeList] = useState<ListType[]>([]);
    const [autoFit, setAutoFit] = useState(true);
    const [lockRatio, setLockRatio] = useState(true);
    const setResolution = useTemplateModel((state) => state.setResolution);
    const originLayers = useRef<ILayerProps[]>([]);
    const originStages = useRef<ILayerProps[][]>([]);
    const originInfo = useRef<ISize & { index: number }>();
    const isInit = useRef(false);

    const limitErrTip =
        $t((v) => v.panel.backgroundPanel.messageError.canvasSizeLimit) +
        `${
            unit === SizeUnitType.px
                ? `${MAX_STAGE_WIDTH} * ${MAX_STAGE_HEIGHT} ${unit}`
                : `${MAX_STAGE_WIDTH_CM * (unit === SizeUnitType.mm ? 10 : 1)} * ${
                      MAX_STAGE_HEIGHT_CM * (unit === SizeUnitType.mm ? 10 : 1)
                  } ${unit}`
        }`;

    // 是否只调整一个画布
    const onlyCurrentStage = editor.services.edgelessCanvas.isSingleMode;

    // 画布切换关闭面板
    useEffect(() => {
        if (isInit.current) {
            onClose?.();
        }
        isInit.current = true;
    }, [activeStageIndex]);

    // 撤销/重做关闭面板
    useEffect(() => {
        const closePanel = () => {
            onClose?.();
        };

        const handle = editor.history.onBeforeChange(({ isRedo, isUndo }) => {
            if (isRedo || isUndo) {
                closePanel();
            }
        });
        events.on(REPLACE_TEMPLATE, closePanel);
        return () => {
            editor.off(handle);
            events.off(REPLACE_TEMPLATE, closePanel);
        };
    }, []);

    // 打开调整尺寸面板，重置缩放比例为自适应
    useEffect(() => {
        if (onlyCurrentStage) {
            editor.setIsAutoScale(true);
        }
    }, []);

    // 选中尺寸列表
    useEffect(() => {
        const handle = editor
            .getInstance<QuickAdjustOperate>('QuickAdjustOperate')
            .onAdjust(({ isStart, isEnd, type }) => {
                // console.log('onQuickAdjust', isStart, isEnd, type);
                if (isStart) {
                    selectSizeHandle(getCustomUnit());
                }
            });
        return () => {
            editor.off(handle);
        };
    }, [sizeList]);

    // 初始化面板数据
    useEffect(() => {
        if (onlyCurrentStage) {
            originLayers.current = cloneDeep(editor.services.frame.getLayers(editor.services.frame.getActiveFrameId()));
        } else {
            originStages.current = cloneDeep(editor.services.frame.getFrames());
        }
        originInfo.current = {
            ...editor.services.frame.getActiveFrameSize(),
            index: editor.services.frame.getActiveIndex(),
        };
    }, []);
    // 获取尺寸列表
    useEffect(() => {
        fetchSizeList({})
            .then((list) => {
                const recommendDefaultList: ListType[] = [
                    {
                        default_name: $t((v) => v.panel.backgroundPanel.originalSize),
                        width: parseToUnit(currentWidth, unit),
                        height: parseToUnit(currentHeight, unit),
                        id: ORIGIN_SIZE_ID,
                        isCheck: true,
                        unit,
                    },
                ];
                const sizeList = handleSizeListStructure(list, recommendDefaultList);
                setSizeList([...sizeList]);
            })
            .catch(() => {});
    }, []);

    // 选择尺寸
    const selectSizeHandle = (item: ListType) => {
        if (item.id !== CUSTOM_SIZE_ID) {
            const { width, height, unit } = item;
            if (unit === SizeUnitType.cm || unit === SizeUnitType.mm) {
                const sizeWidth = width * (unit === SizeUnitType.mm ? 0.1 : 1); //输入框的值
                const sizeHeight = height * (unit === SizeUnitType.mm ? 0.1 : 1); //输入框的值
                if (CHANGE_DPI_WIDTH_EDGE >= sizeWidth && CHANGE_DPI_HEIGHT_EDGE >= sizeHeight) {
                    // 当切换为小于67.7尺寸时，DPI 恢复为300
                    setResolution(300);
                } else {
                    const wDpi = Math.floor(((CHANGE_DPI_WIDTH_EDGE / sizeWidth) * 300) / 10) * 10;
                    const hDpi = Math.floor(((CHANGE_DPI_HEIGHT_EDGE / sizeHeight) * 300) / 10) * 10;
                    setResolution(Math.min(wDpi, hDpi));
                }
            }
            if (!doChangeStageSize(parseToPixels(width, unit), parseToPixels(height, unit), unit)) return;
        }
        sizeList.forEach((val) => {
            val.isCheck = val.id === item.id;
        });
        setSizeList([...sizeList]);
    };

    // 执行调整操作
    const doChangeStageSize = (width: number, height: number, destUnit: SizeUnitType): boolean => {
        if (!originInfo.current) return false;
        // 宽高单位都一致不处理
        if (
            width === currentWidth &&
            height === currentHeight &&
            destUnit === unit &&
            destUnit === SizeUnitType.px // px不变，cm/mm框的值要变
        )
            return true;
        // console.log('canvasSizeLimit', width, height, destUnit);

        // 长图智能排版会按原比例调整其他页的高度，不能只校验当前页的目标尺寸
        const hasInvalidAutoHeight =
            autoFit &&
            !onlyCurrentStage &&
            originStages.current.some((layers, stageIndex) => {
                if (stageIndex === originInfo.current!.index) return false;
                return layers.some((layer) => {
                    if (
                        layer.parentId ||
                        (!editor.services.flatLayer.checkIsBackground(layer) &&
                            !editor.services.flatLayer.checkIsMain(layer))
                    ) {
                        return false;
                    }
                    const nextHeight = Math.ceil(width / (layer.width / layer.height));
                    return (
                        !Number.isFinite(nextHeight) || nextHeight > MAX_STAGE_HEIGHT || nextHeight < MIN_STAGE_HEIGHT
                    );
                });
            });

        if (
            width > MAX_STAGE_WIDTH ||
            width < MIN_STAGE_WIDTH ||
            height > MAX_STAGE_HEIGHT ||
            height < MIN_STAGE_HEIGHT ||
            hasInvalidAutoHeight
        ) {
            Message.error(limitErrTip);
            return false;
        }
        const newSize = { width, height };
        const index = originInfo.current.index;
        const targetSize = {
            width: originInfo.current.width,
            height: originInfo.current.height,
        };
        let targetStages: ILayerProps[][] = [];
        let currentStages: ILayerProps[][] = [];
        if (onlyCurrentStage) {
            targetStages = [cloneDeep(originLayers.current)];
            currentStages = [cloneDeep(editor.services.frame.getLayers(editor.services.frame.getActiveFrameId()))];
        } else {
            targetStages = cloneDeep(originStages.current);
            currentStages = cloneDeep(editor.services.frame.getFrames());
        }
        editor.services.stage[autoFit ? 'scale' : 'resize'](
            onlyCurrentStage ? 0 : index,
            currentStages,
            targetStages,
            targetSize,
            newSize,
            { targetUnit: unit, destUnit },
        );
        setUnit(destUnit);
        editor.services.editor.setUnit(destUnit);
        editor.services.frame.scrollToActiveFrame();
        return true;
    };

    // 宽度改变
    const changeWidthHandle = (value: number) => {
        if (!originInfo.current) return;
        const ratio = currentWidth / currentHeight;
        const sizeWidth = value * (unit === SizeUnitType.mm ? 0.1 : 1); //输入框的值
        let destHeight = currentHeight;
        let sizeHeight = parseFloat(pxToCM(destHeight).toFixed(1));
        // console.log('dpi width', currentWidth, currentHeight, sizeHeight);

        if (unit === SizeUnitType.cm || unit === SizeUnitType.mm) {
            if (sizeWidth > CHANGE_DPI_WIDTH_EDGE && MAX_STAGE_WIDTH_CM >= sizeWidth) {
                //转换dpi，设定67.7内 都是按300分辨率
                let dpi = Math.floor(((CHANGE_DPI_WIDTH_EDGE / sizeWidth) * 300) / 10) * 10;
                if (lockRatio) {
                    const lockHeight = parseFloat((sizeWidth / ratio).toFixed(1));
                    if (MAX_STAGE_HEIGHT_CM >= lockHeight) {
                        //在范围内时计算dpi,否则不做处理，进入到 do change 里做错误提示
                        const hDpi = Math.floor(((CHANGE_DPI_HEIGHT_EDGE / lockHeight) * 300) / 10) * 10;
                        dpi = Math.min(dpi, hDpi);
                    }
                } else {
                    // 非lock时 因为宽度变化导致dpi变化，高度自动计算也会跟着变。只要dpi不是300，此时dpi维持上一次的
                    const preDpi = getDPI();
                    if (dpi < 300) {
                        dpi = Math.min(dpi, preDpi);
                    }
                }
                // 这里设置分辨率后，targetValue = parseToPixels(xx) 获取到值会按最新的分辨率计算
                setResolution(dpi);
            } else if (CHANGE_DPI_WIDTH_EDGE >= sizeWidth) {
                sizeHeight = lockRatio ? parseFloat((sizeWidth / ratio).toFixed(1)) : sizeHeight;
                if (CHANGE_DPI_HEIGHT_EDGE >= sizeHeight) {
                    setResolution(300);
                } else {
                    const dpi = Math.floor(((CHANGE_DPI_HEIGHT_EDGE / sizeHeight) * 300) / 10) * 10;
                    setResolution(dpi);
                }
            } else if (sizeWidth > MAX_STAGE_WIDTH_CM) {
                Message.error(limitErrTip);
            }
        }
        const targetValue = parseToPixels(value, unit);
        if (targetValue > MAX_STAGE_WIDTH || targetValue < MIN_STAGE_WIDTH) {
            Message.error($t((v) => v.panel.backgroundPanel.messageError.autoAdjustedToLimit));
        }
        const destWidth = clamp(targetValue, MIN_STAGE_WIDTH, MAX_STAGE_WIDTH);
        if (lockRatio) {
            destHeight = Math.ceil(destWidth / ratio);
        } else {
            if (unit === SizeUnitType.cm || unit === SizeUnitType.mm) {
                destHeight = cmToPx(sizeHeight);
            }
        }
        doChangeStageSize(destWidth, destHeight, unit);
        // console.log('changeWidthHandle', destWidth, destHeight);
    };

    // 高度改变
    const changeHeightHandle = (value: number) => {
        if (!originInfo.current) return;
        let destWidth = currentWidth;
        const ratio = currentWidth / currentHeight;
        // 特殊处理 cm/mm
        const sizeHeight = value * (unit === SizeUnitType.mm ? 0.1 : 1); //输入框的值
        let sizeWidth = parseFloat(pxToCM(destWidth).toFixed(1));

        if (unit === SizeUnitType.cm || unit === SizeUnitType.mm) {
            if (sizeHeight > CHANGE_DPI_HEIGHT_EDGE && MAX_STAGE_HEIGHT_CM >= sizeHeight) {
                //转换dpi，设定67.7内 都是按300分辨率
                let dpi = Math.floor(((CHANGE_DPI_WIDTH_EDGE / sizeHeight) * 300) / 10) * 10;
                if (lockRatio) {
                    const lockWidth = parseFloat((sizeHeight * ratio).toFixed(1));
                    if (MAX_STAGE_WIDTH_CM >= lockWidth) {
                        //在范围内时计算dpi,否则不做处理，进入到 do change 里做错误提示
                        const wDpi = Math.floor(((CHANGE_DPI_WIDTH_EDGE / lockWidth) * 300) / 10) * 10;
                        dpi = Math.min(dpi, wDpi);
                    }
                } else {
                    const preDpi = getDPI();
                    if (dpi < 300) {
                        dpi = Math.min(dpi, preDpi);
                    }
                }
                // 这里设置分辨率后，targetValue = parseToPixels(xx) 获取到值会按最新的分辨率计算
                setResolution(dpi);
            } else if (CHANGE_DPI_HEIGHT_EDGE >= sizeHeight) {
                sizeWidth = lockRatio ? parseFloat((sizeHeight * ratio).toFixed(1)) : sizeWidth;
                if (CHANGE_DPI_WIDTH_EDGE >= sizeWidth) {
                    setResolution(300);
                    // console.log('dpi恢复为300');
                } else {
                    const dpi = Math.floor(((CHANGE_DPI_WIDTH_EDGE / sizeWidth) * 300) / 10) * 10;
                    setResolution(dpi);
                }
            } else if (sizeHeight > MAX_STAGE_WIDTH_CM) {
                Message.error(limitErrTip);
            }
        }
        const targetValue = parseToPixels(value, unit);
        const destHeight = targetValue;
        if (lockRatio) {
            destWidth = Math.ceil(destHeight * ratio);
        } else {
            if (unit === SizeUnitType.cm || unit === SizeUnitType.mm) {
                destWidth = cmToPx(sizeWidth);
            }
        }
        doChangeStageSize(destWidth, destHeight, unit);
        // console.log('changeHeightHandle', destWidth, destHeight);
    };

    return (
        <div className="c-adjust-size-panel">
            <div style={{ paddingRight: 16 }}>
                <div className="c-adjust-size-panel__title">
                    <BackTitle
                        text={$t((v) => v.panel.backgroundPanel.adjustSize)}
                        handleBackClick={() => {
                            onClose?.();
                        }}
                    />
                    <SelectOpt unit={unit} setUnit={setUnit} />
                </div>

                <SizeInfo
                    widthSize={parseToUnit(currentWidth, unit)}
                    heightSize={parseToUnit(currentHeight, unit)}
                    lockRatio={lockRatio}
                    unit={unit}
                    onChangeHeight={changeHeightHandle}
                    onChangeWidth={changeWidthHandle}
                    onChangeLock={() => {
                        setLockRatio(!lockRatio);
                    }}
                />

                <div className="c-adjust-size-panel-info__tip">
                    <span style={{ padding: '2px 0' }}>
                        <IconFont
                            name="ExclamationMarkCircleBold"
                            fontSize={12}
                            color="var(--content-system-tertiary)"
                        />
                    </span>
                    <div style={{ paddingLeft: 4 }}>
                        {$t((v) => v.panel.backgroundPanel.tips.adjustSizeWouldChangeLayout)}
                    </div>
                </div>

                <div className="c-adjust-size-panel-info__check">
                    <Checkbox
                        checked={autoFit}
                        onChange={(e) => {
                            setAutoFit(e.target.checked);
                        }}
                    >
                        {$t((v) => v.panel.backgroundPanel.smartLayout)}
                    </Checkbox>
                    {isIntl ? (
                        <Tooltip
                            placement="bottom"
                            overlayClassName="c-adjust-size-panel-info__check-tip"
                            title={<div>{$t((v) => v.panel.backgroundPanel.smartLayoutTip)}</div>}
                        >
                            <div className="c-adjust-size-panel-info__check-icon">
                                <IconFont name="QuestionMarkCircleBold" fontSize={14}></IconFont>
                            </div>
                        </Tooltip>
                    ) : null}
                </div>

                <div className="c-adjust-size-panel-line"></div>
            </div>
            <List
                sizeList={sizeList}
                onSelectSize={selectSizeHandle}
                currentWidth={currentWidth}
                currentHeight={currentHeight}
            />
        </div>
    );
};

export default React.memo(AdjustSizePanel);
